import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';

import { hzToMhz } from '../lib/frequency.js';
import { formatNumber, parseKvNumber } from '../lib/number-format.js';
import { type DetectionGroup, SignalType } from '../types';
import { useIntl } from './useIntl.js';
import { type SuppressionChannelPayload, useCreateSuppression } from './useSuppressions.js';

export type UseLinkedDeviceSuppressOptions = {
  deviceId: string;
  onSuccess?: () => void | Promise<void>;
};

export type UseLinkedDeviceSuppressResult = {
  message: string | null;
  clearMessage: () => void;
  suppressFromLinkedDevice: (
    group: DetectionGroup,
    linkedDeviceSerial: string,
    durationSec: number,
    priority?: number,
  ) => Promise<void>;
};

export function useLinkedDeviceSuppress({
  deviceId,
  onSuccess,
}: UseLinkedDeviceSuppressOptions): UseLinkedDeviceSuppressResult {
  const intl = useIntl();
  const createSuppression = useCreateSuppression();
  const [message, setMessage] = useState<string | null>(null);

  // Reset message when deviceId changes (Decision 5 path 2)
  useEffect(() => {
    setMessage(null);
  }, [deviceId]);

  // 4s auto-dismiss (donor :561-565)
  useEffect(() => {
    if (!message) return;
    const timeout = window.setTimeout(() => setMessage(null), 4000);
    return () => window.clearTimeout(timeout);
  }, [message]);

  const clearMessage = useCallback(() => setMessage(null), []);

  const suppressFromLinkedDevice = async (
    group: DetectionGroup,
    linkedDeviceSerial: string,
    durationSec: number,
    priority?: number,
  ): Promise<void> => {
    try {
      // Build payload per donor :426-447, linked-device path (conditional keys — hard requirement R2)
      const channelPayload: SuppressionChannelPayload = {
        channelFrequency: group.frequency,
        type: group.signalType as SignalType,
        duration: durationSec,
        offset: parseKvNumber(group.offset) ?? 0,
      };

      if (priority !== undefined) {
        channelPayload.priority = priority;
      }

      channelPayload.parentDetectionGroupId = group.id;
      channelPayload.parentDetectionGroupCreatedAt = group.createdAt;

      await createSuppression.mutateAsync({
        deviceId,
        payload: {
          channels: [channelPayload],
        },
      });

      await onSuccess?.();

      setMessage(
        intl.formatMessage(
          { id: 'detections.related.suppressionCreated' },
          {
            serial: linkedDeviceSerial,
            frequency: formatNumber(hzToMhz(group.frequency), {
              maximumFractionDigits: 5,
            }),
          },
        ),
      );
    } catch (error) {
      Sentry.captureException(error);
      console.error('Failed to suppress from linked device', error);
    }
  };

  return { message, clearMessage, suppressFromLinkedDevice };
}
